import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FestivalHeader } from '@/components/timetable/FestivalHeader';
import { ThemeToggle } from '@/components/ThemeToggle';
import { formatDuration } from '@/lib/timetable';
import {
  type ProgramTimetable,
  type StaffShiftWithAssignment,
  type TimetablePerformance,
} from '@littlebooker/dal';
import { apiGetPublic } from '@/lib/api';

const SLOT_MINUTES = 30;
const SLOT_SIZE_PX = 60;
const TRACK_HEIGHT = 36;
const STAGE_HEADER_HEIGHT = 36;
const LABEL_WIDTH = 200;
const slotCount = (24 * 60) / SLOT_MINUTES;

const bannerStyles = {
  warning: 'bg-amber-50 text-amber-900 border-amber-100',
  error: 'bg-red-50 text-red-900 border-red-100',
} as const;

function StatusBanner({ title, body, variant }: { title: string; body: string; variant: keyof typeof bannerStyles }) {
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm/relaxed opacity-90">{body}</p>
      </div>
    </div>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
        <div className="relative">
          <div className="absolute right-0 top-0 z-10">
            <ThemeToggle />
          </div>
          <div className="space-y-6 pr-12">{children}</div>
        </div>
        <footer className="mt-10 border-t pt-4 text-center text-xs text-muted-foreground">
          Powered by{' '}
          <a
            href="https://littlestreamer.nl/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:text-foreground hover:underline"
          >
            LittleBooker
          </a>
        </footer>
      </div>
    </div>
  );
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const fmtTime = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

// Pack time-overlapping items into stacked sub-lanes so they don't visually collide.
function packLanes<T extends { start_time: string | null; end_time: string | null }>(
  items: T[],
): { lanes: Map<T, number>; count: number } {
  const startOf = (it: T) => Date.parse(it.start_time ?? '') || 0;
  const endOf = (it: T) => Date.parse(it.end_time ?? it.start_time ?? '') || startOf(it);
  const sorted = [...items].sort((a, b) => startOf(a) - startOf(b));
  const laneEnds: number[] = [];
  const lanes = new Map<T, number>();
  for (const it of sorted) {
    const start = startOf(it);
    const end = endOf(it);
    let lane = laneEnds.findIndex((e) => e <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    lanes.set(it, lane);
  }
  return { lanes, count: Math.max(1, laneEnds.length) };
}

function TimetableGrid({ data }: { data: ProgramTimetable }) {
  const programStart = new Date(data.program.start_time);
  const programEnd = new Date(data.program.end_time);

  const days = useMemo(() => {
    const out: Date[] = [];
    const cur = startOfDay(programStart);
    const last = startOfDay(programEnd);
    while (cur <= last) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out.length > 0 ? out : [startOfDay(programStart)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.program.start_time, data.program.end_time]);

  const [activeDayIdx, setActiveDayIdx] = useState(0);

  // Continuous timeline across the whole programme (all days as one scrollable
  // axis) so cross-midnight shifts and acts render as one unbroken bar. Day pills
  // scroll to a day rather than switching the view.
  const timelineStart = startOfDay(days[0] ?? programStart).getTime();
  const dayCount = days.length;
  const totalMinutes = dayCount * 24 * 60;
  const totalSlots = dayCount * slotCount;
  const dayWidthPx = slotCount * SLOT_SIZE_PX;
  const gridScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollToDay = (idx: number) => {
    setActiveDayIdx(idx);
    gridScrollRef.current?.scrollTo({ left: idx * dayWidthPx, behavior: 'smooth' });
  };
  const onGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const idx = Math.max(0, Math.min(dayCount - 1, Math.round(e.currentTarget.scrollLeft / dayWidthPx)));
    setActiveDayIdx((cur) => (cur === idx ? cur : idx));
  };

  const placeName = (id: string) => data.places.find((p) => p.id === id)?.name ?? 'Stage';
  const roleName = (id: string) => data.roles.find((r) => r.id === id)?.name ?? 'Role';

  // Stages that have any shift, in a stable order.
  const stageIds = useMemo(() => [...new Set(data.shifts.map((s) => s.place_id))], [data.shifts]);

  const visibleShifts = data.shifts;

  const shiftsByStageRole = useMemo(() => {
    const byStage = new Map<string, Map<string, StaffShiftWithAssignment[]>>();
    for (const s of visibleShifts) {
      const roleMap = byStage.get(s.place_id) ?? byStage.set(s.place_id, new Map()).get(s.place_id)!;
      (roleMap.get(s.role_id) ?? roleMap.set(s.role_id, []).get(s.role_id)!).push(s);
    }
    return byStage;
  }, [visibleShifts]);

  const performancesByStage = useMemo(() => {
    const byStage = new Map<string, TimetablePerformance[]>();
    for (const p of data.performances) {
      if (!p.place_id || !p.start_time) continue;
      (byStage.get(p.place_id) ?? byStage.set(p.place_id, []).get(p.place_id)!).push(p);
    }
    return byStage;
  }, [data.performances]);

  const slots = Array.from({ length: totalSlots }, (_, i) => new Date(timelineStart + i * SLOT_MINUTES * 60000));
  const minutesIntoTimeline = (iso: string) => Math.max(0, (Date.parse(iso) - timelineStart) / 60000);
  const laneStyle = (lane: number) => ({ top: lane * TRACK_HEIGHT + 3, height: TRACK_HEIGHT - 6 });

  const renderPerformance = (p: TimetablePerformance, lane: number) => {
    if (!p.start_time) return null;
    const startMin = minutesIntoTimeline(p.start_time);
    const endMin = Math.min(totalMinutes, minutesIntoTimeline(p.end_time ?? p.start_time) || startMin + 60);
    const left = (startMin / SLOT_MINUTES) * SLOT_SIZE_PX;
    const width = Math.max(SLOT_SIZE_PX / 2, ((endMin - startMin) / SLOT_MINUTES) * SLOT_SIZE_PX);
    const label = p.artists.length > 0 ? `${p.title} — ${p.artists.join(', ')}` : p.title;
    return (
      <div
        key={p.id}
        className="absolute overflow-hidden rounded border border-sky-500/40 bg-sky-500/10 px-2 text-xs text-sky-900 dark:text-sky-200"
        style={{ left, width, ...laneStyle(lane) }}
        title={label}
      >
        <div className="truncate font-medium">{label}</div>
      </div>
    );
  };

  const renderShiftBar = (s: StaffShiftWithAssignment, lane: number) => {
    const startMin = Math.max(0, (Date.parse(s.start_time) - timelineStart) / 60000);
    const endMin = Math.min(totalMinutes, (Date.parse(s.end_time) - timelineStart) / 60000);
    const left = (startMin / SLOT_MINUTES) * SLOT_SIZE_PX;
    const width = Math.max(SLOT_SIZE_PX / 2, ((endMin - startMin) / SLOT_MINUTES) * SLOT_SIZE_PX);
    const assigned = !!s.assignment;
    return (
      <div
        key={s.id}
        className={
          'absolute overflow-hidden rounded border px-2 text-xs ' +
          (assigned ? 'border-emerald-500/50 bg-emerald-500/15' : 'border-muted-foreground/30 bg-muted/40')
        }
        style={{ left, width, ...laneStyle(lane) }}
        title={`${roleName(s.role_id)} · ${fmtTime(new Date(s.start_time))}–${fmtTime(new Date(s.end_time))}`}
      >
        <div className="truncate font-medium">{assigned ? s.person_name ?? 'Assigned' : 'Unassigned'}</div>
        <div className="truncate text-[10px] text-muted-foreground">
          {fmtTime(new Date(s.start_time))}–{fmtTime(new Date(s.end_time))} ({formatDuration(s.start_time, s.end_time)})
        </div>
      </div>
    );
  };

  const laneHeight = (count: number) => count * TRACK_HEIGHT;

  return (
    <div className="space-y-4">
      {days.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {days.map((d, idx) => (
            <Button
              key={idx}
              size="sm"
              variant={idx === activeDayIdx ? 'default' : 'outline'}
              onClick={() => scrollToDay(idx)}
            >
              {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
            </Button>
          ))}
        </div>
      )}

      {data.shifts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No shifts have been scheduled yet.</p>
      ) : (
        <div ref={gridScrollRef} onScroll={onGridScroll} className="overflow-auto rounded-md border" style={{ maxHeight: '75vh' }}>
          <div style={{ minWidth: LABEL_WIDTH + slots.length * SLOT_SIZE_PX }}>
            {/* Time axis: a day band over a row of half-hour labels */}
            <div className="sticky top-0 z-20 border-b bg-muted/95 text-xs text-muted-foreground backdrop-blur">
              {/* Day band — marks where each day starts on the continuous axis */}
              <div className="flex border-b border-border/40">
                <div className="sticky left-0 z-10 shrink-0 border-r bg-muted/95" style={{ width: LABEL_WIDTH }} />
                <div className="flex">
                  {days.map((d, idx) => (
                    <div
                      key={idx}
                      style={{ width: dayWidthPx }}
                      className="shrink-0 border-l-2 border-foreground/30 px-2 py-1 font-medium first:border-l-0"
                    >
                      {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                  ))}
                </div>
              </div>
              {/* Half-hour labels */}
              <div className="flex">
                <div className="sticky left-0 z-10 shrink-0 border-r bg-muted/95 p-2 font-medium" style={{ width: LABEL_WIDTH }}>
                  Stage / role
                </div>
                <div className="flex">
                  {slots.map((slot, idx) => (
                    <div
                      key={idx}
                      style={{ width: SLOT_SIZE_PX }}
                      className={
                        'shrink-0 border-r p-1 text-center ' +
                        (slot.getHours() === 0 && slot.getMinutes() === 0 ? 'border-l-2 border-foreground/30' : '')
                      }
                    >
                      {slot.getMinutes() === 0 ? fmtTime(slot) : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {stageIds.map((stageId) => {
              const roleMap = shiftsByStageRole.get(stageId);
              const roleIds = roleMap ? Array.from(roleMap.keys()) : [];
              const stagePerformances = performancesByStage.get(stageId) ?? [];
              const trackWidth = slots.length * SLOT_SIZE_PX;
              const perfPack = packLanes(stagePerformances);
              const rolePacks = new Map(roleIds.map((rid) => [rid, packLanes(roleMap!.get(rid) ?? [])]));
              const showPerfLane = data.program.lineup_revealed;
              return (
                <div key={stageId} className="flex border-b last:border-b-0">
                  <div className="sticky left-0 z-10 shrink-0 border-r bg-background" style={{ width: LABEL_WIDTH }}>
                    <div className="border-b p-2 text-sm font-medium" style={{ height: STAGE_HEADER_HEIGHT }}>
                      {placeName(stageId)}
                    </div>
                    {showPerfLane && (
                      <div
                        className="flex items-center border-b border-border/30 px-2 text-xs italic text-muted-foreground"
                        style={{ height: laneHeight(perfPack.count) }}
                      >
                        Line-up
                      </div>
                    )}
                    {roleIds.length === 0 ? (
                      <div className="px-2 text-xs text-muted-foreground" style={{ height: TRACK_HEIGHT, lineHeight: `${TRACK_HEIGHT}px` }}>
                        No shifts
                      </div>
                    ) : (
                      roleIds.map((rid) => (
                        <div
                          key={rid}
                          className="flex items-center border-b border-border/30 px-2 text-xs text-muted-foreground last:border-b-0"
                          style={{ height: laneHeight(rolePacks.get(rid)!.count) }}
                        >
                          {roleName(rid)}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="relative" style={{ width: trackWidth }}>
                    {/* Day-boundary divider lines down the whole stage block */}
                    {Array.from({ length: dayCount - 1 }, (_, i) => (
                      <div
                        key={`day-divider-${i}`}
                        className="pointer-events-none absolute inset-y-0 z-0 border-l-2 border-foreground/20"
                        style={{ left: (i + 1) * dayWidthPx }}
                      />
                    ))}
                    <div className="border-b" style={{ height: STAGE_HEADER_HEIGHT }} />
                    {showPerfLane && (
                      <div className="relative border-b border-border/30" style={{ height: laneHeight(perfPack.count) }}>
                        {stagePerformances.map((p) => renderPerformance(p, perfPack.lanes.get(p) ?? 0))}
                      </div>
                    )}
                    {roleIds.length === 0 ? (
                      <div style={{ height: TRACK_HEIGHT }} />
                    ) : (
                      roleIds.map((rid) => {
                        const pack = rolePacks.get(rid)!;
                        return (
                          <div key={rid} className="relative border-b border-border/30 last:border-b-0" style={{ height: laneHeight(pack.count) }}>
                            {(roleMap!.get(rid) ?? []).map((s) => renderShiftBar(s, pack.lanes.get(s) ?? 0))}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicTimetablePage() {
  const { programSlug } = useParams<{ programSlug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { data, isLoading, isError, error } = useQuery({
    // Fetch via the public API (server validates the token + bypasses RLS).
    queryKey: ['public-timetable', programSlug, token],
    queryFn: () =>
      apiGetPublic<ProgramTimetable>(`/timetable/${encodeURIComponent(programSlug ?? '')}`, { token }),
    enabled: Boolean(programSlug && token),
  });

  if (!programSlug || !token) {
    return (
      <PageShell>
        <StatusBanner
          variant="warning"
          title="Link incomplete"
          body="This timetable link is missing part of its address. Ask your festival organiser to resend it."
        />
      </PageShell>
    );
  }

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading timetable…
        </div>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <StatusBanner
          variant="error"
          title="Something went wrong"
          body={error instanceof Error ? error.message : 'We could not load the timetable. Try again later.'}
        />
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell>
        <StatusBanner
          variant="error"
          title="Link not valid"
          body="This timetable link isn't recognised. Ask your festival organiser for a fresh link."
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="space-y-2">
        <FestivalHeader
          name={data.program.name}
          startTime={data.program.start_time}
          endTime={data.program.end_time}
          location={data.program.location}
        />
        <p className="text-sm text-muted-foreground">
          Staff timetable{data.program.lineup_revealed ? ' · line-up revealed' : ''}
        </p>
        {data.program.contact && (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {data.program.contact.name && (
              <span>
                Shifts put together by <span className="font-medium text-foreground">{data.program.contact.name}</span>.
              </span>
            )}
            {data.program.contact.email && (
              <a href={`mailto:${data.program.contact.email}`} className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline">
                <Mail className="h-3.5 w-3.5" />
                {data.program.contact.email}
              </a>
            )}
            {data.program.contact.phone && (
              <a href={`tel:${data.program.contact.phone}`} className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline">
                <Phone className="h-3.5 w-3.5" />
                {data.program.contact.phone}
              </a>
            )}
          </p>
        )}
      </div>
      <TimetableGrid data={data} />
    </PageShell>
  );
}
